import { useState, useCallback, useRef } from 'react';
import { devLog, devWarn, devError } from '../utils/devLogger';
import { likeLot } from '../lib/api/user/userApi';

export interface FavoriteUpdateEvent {
  lotId: number;
  isLiked: boolean;
  likedUserIndexes: number[];
}

export interface FavoriteManager {
  handleLikeClick: (lotId: number, currentUserIndex?: number, currentLikedUserIndexes?: number[]) => Promise<void>;
  onFavoriteUpdate: (callback: (event: FavoriteUpdateEvent) => void) => () => void;
  isProcessing: boolean;
}

export const useFavoriteManager = (): FavoriteManager => {
  const [isProcessing, setIsProcessing] = useState(false);
  const updateCallbacksRef = useRef<((event: FavoriteUpdateEvent) => void)[]>([]);

  // 좋아요 상태 업데이트 콜백 등록
  const onFavoriteUpdate = useCallback((callback: (event: FavoriteUpdateEvent) => void) => {
    updateCallbacksRef.current.push(callback);
    
    // cleanup 함수 반환
    return () => {
      updateCallbacksRef.current = updateCallbacksRef.current.filter(cb => cb !== callback);
    };
  }, []);

  // 좋아요 처리 함수
  const handleLikeClick = useCallback(async (
    lotId: number, 
    currentUserIndex?: number, 
    currentLikedUserIndexes?: number[]
  ) => {
    if (isProcessing) return;
    
    setIsProcessing(true);

    let newLikedUserIndexes: number[] = [];
    let isLiked = false;
    
    if (currentUserIndex !== undefined && currentLikedUserIndexes) {
      if (currentLikedUserIndexes.includes(currentUserIndex)) {
        newLikedUserIndexes = currentLikedUserIndexes.filter(index => index !== currentUserIndex);
      } else {
        newLikedUserIndexes = [...currentLikedUserIndexes, currentUserIndex];
        isLiked = true;
      }
    }

    const updateEvent: FavoriteUpdateEvent = {
      lotId,
      isLiked,
      likedUserIndexes: newLikedUserIndexes
    };
    updateCallbacksRef.current.forEach((callback: (event: FavoriteUpdateEvent) => void) => callback(updateEvent));

    void likeLot(lotId)
      .catch((error) => {
        devError('좋아요 처리 실패:', error);
      })
      .finally(() => {
        setIsProcessing(false);
      });
  }, [isProcessing]);

  return {
    handleLikeClick,
    onFavoriteUpdate,
    isProcessing
  };
};
